import { Cookie } from "lucide-react";
import { type JSX, useEffect, useState } from "react";

import { useIntlMessages } from "@/app/hooks/useIntlMessages";
import { Button } from "@/components/ui/button";
import { useGetUserConsentQuery } from "@/features/cookieConsent/cookieConsent.api";
import { cookieConsentMessages } from "@/features/cookieConsent/cookieConsent.intl";
import {
  acceptConsent,
  hydrateConsentFromServer,
} from "@/features/cookieConsent/cookieConsent.slice";
import { useAppDispatch, useAppSelector } from "@/redux/store";

type CookieConsentBannerProps = {
  enabled: boolean;
};

/**
 * Cookie Consent Banner — shown until the workspace cookie is accepted.
 * @param {CookieConsentBannerProps} props Component props.
 * @param {boolean} props.enabled Whether cookie consent is enabled for the current route.
 * @returns The consent banner JSX or null when consent has already been given.
 */
const CookieConsentBanner = ({
  enabled,
}: CookieConsentBannerProps): JSX.Element | null => {
  const t = useIntlMessages(cookieConsentMessages);
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) =>
    Boolean(state.user.userToken),
  );
  const consentGiven = useAppSelector(
    (state) => state.cookieConsent.consentGiven,
  );

  const {
    data: serverConsent,
    isFetching,
    isLoading,
    isUninitialized,
  } = useGetUserConsentQuery(undefined, {
    skip: !enabled || !isAuthenticated || consentGiven,
  });
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (!serverConsent?.consentGiven) {
      return;
    }

    dispatch(
      hydrateConsentFromServer({
        consentGiven: serverConsent.consentGiven,
        lastUpdated: serverConsent.lastUpdated,
        consentVersion: serverConsent.consentVersion,
      }),
    );
  }, [dispatch, serverConsent]);

  const isWaitingForServerConsent = isUninitialized || isLoading || isFetching;

  if (
    !hasMounted ||
    !enabled ||
    consentGiven ||
    serverConsent?.consentGiven ||
    isWaitingForServerConsent
  ) {
    return null;
  }

  return (
    <div
      role='dialog'
      aria-modal='false'
      aria-label='Cookie consent'
      aria-describedby='cookie-consent-description'
      className='fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6 bg-background border-t border-border shadow-2xl animate-in slide-in-from-bottom-4 duration-300'
    >
      <div className='max-w-6xl mx-auto flex flex-col gap-4 md:flex-row md:items-center md:gap-6'>
        {/* Icon + text */}
        <div className='flex items-start gap-3 flex-1 min-w-0'>
          <Cookie
            className='text-primary mt-0.5 shrink-0'
            size={22}
            strokeWidth={1.5}
            aria-hidden='true'
          />
          <div id='cookie-consent-description' className='min-w-0'>
            <p className='text-sm font-semibold text-foreground mb-1'>
              {t("bannerTitle")}
            </p>
            <p className='text-xs text-muted-foreground leading-relaxed max-w-3xl'>
              {t("bannerDescription")}
            </p>
          </div>
        </div>

        <div className='flex flex-wrap gap-2 shrink-0'>
          <Button
            size='sm'
            onClick={() => dispatch(acceptConsent(new Date().toISOString()))}
            className='text-xs'
            aria-label='Accept workspace cookie'
          >
            {t("acceptButton")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
